import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ChatPage.css';
import api from '../api'; // Use the centralized api instance
import { useGenies } from './GenieContext';
import TableView from './TableView';
import VegaChart from './VegaChart';
import { useConversations } from '../hooks/useConversations';
import { exportAsPDF, exportAsCSV, exportAsTXT } from '../utils/exportChat';

// ─── Hardcoded initial questions per genie (matched by name substring) ───────
const GENIE_INITIAL_QUESTIONS = {
  'maintenance analyst': [
    'How many maintenance items were completed last year across all shop locations?',
    'What is the average duration assets stay under repair in the shop?',
    'What is the average time from open to close of a maintenance item by maintenance shop?',
  ],
  'comeback': [
    'What is the most common repair code for comeback repairs?',
    'How many comeback repairs did I have quarter over quarter last year?',
    'What are the top 5 assets with comeback repairs last year?',
  ],
  'assets': [
    'Give me a snapshot of how many assets I have in each status.',
    'How many assets have expired warranties versus active warranties?',
    'What types of vehicles make up my fleet?',
  ],
  'parts inventory': [
    'Which parts are at risk of running out soon, and which locations need replenishment?',
    'What parts are used most frequently, and how should we adjust reorder points or stocking levels?',
    'What parts do we need for upcoming PMs?',
    'What is the current inventory count for each Storeroom?',
  ],
  'lifecycle cost': [
    'Which assets have the highest total lifecycle cost, and what are the primary cost drivers?',
    'When should we replace an asset to minimize lifecycle cost while maintaining reliable service?',
    'How do lifecycle costs compare across asset types, manufacturers, models, or operating locations?',
  ],
};

/**
 * Returns the hardcoded initial questions for a genie based on its name.
 * Priority order: "lifecycle cost" → "maintenance analyst" → "comeback" → "assets / warranty" → "parts inventory"
 */
function getInitialQuestions(genieName) {
  if (!genieName) return [];
  const lower = genieName.toLowerCase();
  if (lower.includes('lifecycle cost')) return GENIE_INITIAL_QUESTIONS['lifecycle cost'];
  if (lower.includes('maintenance analyst')) return GENIE_INITIAL_QUESTIONS['maintenance analyst'];
  if (lower.includes('comeback')) return GENIE_INITIAL_QUESTIONS['comeback'];
  if (lower.includes('assets') || lower.includes('warranty')) return GENIE_INITIAL_QUESTIONS['assets'];
  if (lower.includes('parts inventory')) return GENIE_INITIAL_QUESTIONS['parts inventory'];
  // Fallback: plain "maintenance" without "analyst" suffix
  if (lower.includes('maintenance')) return GENIE_INITIAL_QUESTIONS['maintenance analyst'];
  return [];
}

function GenericChatPage() {
  const navigate = useNavigate();
  const { genieId: genieIdParam } = useParams(); // from /chat/:genieId route
  const context = useGenies();
  
  // Safely destructure with defaults to prevent crashes
  // useMemo ensures the fallback [] and () => {} references are stable across renders,
  // preventing the useEffect at line 44 from re-running unnecessarily.
  const genies = useMemo(() => context?.genies || [], [context?.genies]);
  const selectedGenie = context?.selectedGenie || null;
  const setSelectedGenie = useMemo(
    () => context?.setSelectedGenie || (() => {}),
    [context?.setSelectedGenie]
  );
  const isGenieListLoading = context?.isLoading || false;
  const isAuthenticated = context?.isAuthenticated || false;
  const currentUser = context?.currentUser || null;
  const selectedClient = context?.selectedClient || null;
  const isProcessing = context?.isProcessing || false;
  const setIsProcessing = useMemo(
    () => context?.setIsProcessing || (() => {}),
    [context?.setIsProcessing]
  );

  const genieId = selectedGenie?.id;

  // When navigating from the Genies page via /chat/:genieId, sync the context
  // selectedGenie to the genie specified in the URL param. This runs only when
  // the URL param or the genies list changes — NOT when selectedGenie changes.
  //
  // Critically, selectedGenie must NOT be in the dependency array here. If it were,
  // this effect would re-run every time the user picks a different genie from the
  // dropdown (because handleGenieChange calls setSelectedGenie), see the
  // selectedGenie?.id === genieIdParam guard fail, and force the selection back to
  // the URL param genie — undoing the user's choice.
  //
  // Using a ref to read the current selection inside the effect lets us still
  // guard against unnecessary setSelectedGenie calls without adding selectedGenie
  // to the dep array.
  const selectedGenieIdRef = useRef(selectedGenie?.id);
  useEffect(() => {
    selectedGenieIdRef.current = selectedGenie?.id;
  });

  useEffect(() => {
    if (!genieIdParam) return; // no URL param — plain /chat route, leave as-is
    if (isGenieListLoading) return; // wait until genies are loaded
    if (genies.length === 0) return; // no genies yet
    if (selectedGenieIdRef.current === genieIdParam) return; // already correct, nothing to do

    const match = genies.find(g => g.id === genieIdParam);
    if (match) {
      setSelectedGenie(match);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genieIdParam, genies, isGenieListLoading, setSelectedGenie]);

  // Extract display name from user data (same logic as Navbar)
  const getDisplayName = () => {
    if (!currentUser) return 'User';
    
    // If displayName exists and is not an email, use it
    if (currentUser.displayName && !currentUser.displayName.includes('@')) {
      return currentUser.displayName;
    }
    
    // If we have mail, extract first part before @
    if (currentUser.mail) {
      const emailPart = currentUser.mail.split('@')[0];
      // Capitalize first letter and replace dots/underscores with spaces
      return emailPart
        .split(/[._]/)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    
    return 'User';
  };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  // Export state
  const [isExportOpen, setIsExportOpen] = useState(false);
  // copiedMsgIndex: index of the message whose copy button shows "Copied!" feedback
  const [copiedMsgIndex, setCopiedMsgIndex] = useState(null);
  const exportBtnRef = useRef(null);
  const recognitionRef = useRef(null);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [databricksConversationId, setDatabricksConversationId] = useState(null);
  const [loadingConversationId, setLoadingConversationId] = useState(null);
  // Track which conversation is being deleted so the button shows a spinner
  const [deletingConversationId, setDeletingConversationId] = useState(null);
  // feedback: { [groupId]: 'POSITIVE' | 'NEGATIVE' | 'submitting' | null }
  const [feedbackState, setFeedbackState] = useState({});
  const messagesEndRef = useRef(null);
  const isProcessingRef = useRef(false); // Prevent duplicate sends
  // Stable ref for messages — lets handleSend read the latest messages array
  // without adding `messages` to its useCallback dep array (Bug 4 fix).
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; });
  
  // Use conversation history hook
  const {
    conversations,
    loadConversations,
    loadConversation,
    createConversation,
    updateConversationActivity,
    deleteConversation
  } = useConversations();
  

  const handleFeedback = useCallback(async (groupId, spaceId, convId, msgId, rating) => {
    if (!convId || !msgId) return;

    // Optimistic update — show the new vote immediately, no spinner wait
    setFeedbackState(prev => {
      if (prev[groupId] === 'submitting') return prev; // guard in-flight only
      return { ...prev, [groupId]: rating };
    });

    // Fire API in background — revert on error
    api.post('/api/genie/feedback', {
      spaceId,
      conversationId: convId,
      messageId: msgId,
      rating
    }).catch(err => {
      console.error('[FEEDBACK] Failed to submit feedback:', err);
      // Revert to unrated so user can try again
      setFeedbackState(prev => ({ ...prev, [groupId]: null }));
    });
  }, []);

  // Reset Databricks conversation ID whenever the client changes, so a new
  // conversation is started for the new client rather than sending a stale ID
  // from a previous client's session.
  const prevClientRef = useRef(selectedClient);
  useEffect(() => {
    if (prevClientRef.current !== selectedClient) {
      prevClientRef.current = selectedClient;
      setDatabricksConversationId(null);
      setMessages([]);
      setActiveConversationId(null);
    }
  }, [selectedClient]);

  const handleSend = useCallback(async (predefinedQuestion = null) => {
    const currentInput = predefinedQuestion || input;
    // Ensure currentInput is a string
    const inputText = typeof currentInput === 'string' ? currentInput : String(currentInput);
    if (inputText.trim() === '' || isWaitingForResponse || !selectedGenie) return;
    
    // [SPN DEBUG] Log client/genie context at send time
    // eslint-disable-next-line no-unused-vars
    const resolvedClient = selectedClient || localStorage.getItem('selectedClient');
    
    // Prevent duplicate sends (React Strict Mode issue)
    if (isProcessingRef.current) {
      return;
    }
    isProcessingRef.current = true;

    // Read messages via ref so `messages` is NOT in the useCallback dep array.
    // Adding `messages` as a dep would recreate handleSend on every response,
    // making startNewChat's reference go stale and causing infinite re-renders
    // when startNewChat calls handleSend (Bug 4 fix).
    const messagesWithoutSuggestions = messagesRef.current.filter(m => m.type !== 'suggestions');

    const userMessage = { role: 'user', content: inputText };
    const statusMessage = { role: 'status', content: 'Processing...' };
    setMessages([...messagesWithoutSuggestions, userMessage, statusMessage]);
    if (!predefinedQuestion) setInput('');
    setIsWaitingForResponse(true);
    setIsProcessing(true);
    setIsDropdownOpen(false); // Force-close the genie dropdown if it was open

    try {
        // Build conversation context from recent messages (last 5 exchanges = 10 messages)
        const recentMessages = messagesWithoutSuggestions
            .filter(m => m.role === 'user' || (m.role === 'assistant' && m.content))
            .slice(-10) // Last 10 messages (5 exchanges)
            .map(m => ({
                role: m.role,
                content: m.content
            }));
        
        // Prepare request with context and conversation ID
        const requestData = {
            spaceId: genieId,
            question: inputText,
            clientName: selectedClient || localStorage.getItem('selectedClient') || undefined
        };
        
        // Add Databricks conversation ID to continue existing conversation
        if (databricksConversationId) {
            requestData.databricksConversationId = databricksConversationId;
        } else {
        }
        
        // Add conversation context only when starting a NEW conversation.
        // When continuing an existing Databricks conversation (databricksConversationId
        // is set), Databricks already tracks the full history natively — injecting
        // context as a text preamble would cause double-context and confuse the model
        // (Bug 5 fix).
        if (recentMessages.length > 0 && !databricksConversationId) {
            requestData.conversationContext = recentMessages;
        }
        
        // [SPN DEBUG] Log exact payload being sent to backend
        console.log('[SPN DEBUG] Payload being sent to backend:', {
            spaceId: requestData.spaceId,
            clientName: requestData.clientName,
            hasDatabricksConversationId: !!requestData.databricksConversationId,
            contextMessages: requestData.conversationContext?.length || 0
        });
        
        const response = await api.post('/api/genie/ask', requestData);
        
        // [SPN DEBUG] Log what SPN the backend actually used


        // Note: conversationId from response is Databricks's ID, not ours
        const { content, charts, attachments, conversationId: databricksConvId, messageId, preEnrichedAttachments, multiSPNResult, enhanced_visualizations, spnUsed, spnsUsed } = response.data;
        
        // Store/update Databricks conversation ID
        if (databricksConvId) {
            setDatabricksConversationId(databricksConvId);
        }
        
        // Resolve which SPN to store for history lookup.
        // - Single-SPN response: spnUsed is set directly.
        // - Multi-SPN response: spnsUsed is an array; pick the first entry as the
        //   "canonical" SPN. The history fetch will fall back to trying all candidates
        //   anyway for legacy rows, so any entry from the array is correct.
        // - If neither is set (unexpected), store null so the fallback kicks in.
        const resolvedSpnName = spnUsed || (Array.isArray(spnsUsed) && spnsUsed.length > 0 ? spnsUsed[0] : null);
        
        // Create conversation metadata in DB on FIRST message of a NEW conversation.
        // Guard: messagesWithoutSuggestions.length === 0 means we started fresh;
        //        databricksConvId must be present (Databricks created a conversation).
        // spnUsed is stored so history fetch uses the same SPN — Databricks enforces
        // conversation ownership (403 if the wrong SPN tries to read it).
        // clientName is included so that history is isolated per user > client > genie.
        let conversationId = activeConversationId;
        if (messagesWithoutSuggestions.length === 0 && databricksConvId) {
            const newConv = await createConversation(
                genieId,
                selectedGenie.name,
                inputText.substring(0, 100), // First 100 chars as title
                databricksConvId,            // Databricks conversation ID
                genieId,                     // databricks_space_id
                selectedClient || null,      // client_name isolation key
                resolvedSpnName              // SPN that created the conversation
            );
            if (newConv) {
                conversationId = newConv.id;
                setActiveConversationId(conversationId);
            }
        } else if (conversationId) {
            // Subsequent message in an existing conversation — bump updated_at so
            // the sidebar shows the accurate "last used" time instead of the creation time.
            updateConversationActivity(conversationId);
        }
        
        const feedbackGroupId = `fg_${Date.now()}`;
        const newMessages = [];
        const processedAttachmentIds = new Set();
        let assistantResponseText = null;

        // For multi-SPN results, use the combined content directly
        if (multiSPNResult && content) {
            assistantResponseText = content;
        } else {
            // Priority 1: Find a text attachment for the main response.
            const textAttachment = attachments?.find(att => att.text && att.text.content);
            if (textAttachment) {
                assistantResponseText = textAttachment.text.content;
                processedAttachmentIds.add(textAttachment.attachment_id);
            }

            // Priority 2: Find a description from a query attachment if no primary text was found.
            if (!assistantResponseText) {
                const queryAttachmentWithDesc = attachments?.find(att => att.query && att.query.description);
                if (queryAttachmentWithDesc) {
                    assistantResponseText = queryAttachmentWithDesc.query.description;
                }
            }
        }
        
        // Add the determined text response to the message list.
        if (assistantResponseText) {
            newMessages.push({ role: 'assistant', content: assistantResponseText });
        }
        
        // Process enhanced visualizations first (new system)
        if (enhanced_visualizations && enhanced_visualizations.length > 0) {
            for (const vis of enhanced_visualizations) {
                // Backend sends 'spec', not 'vega_spec'
                if (vis.spec) {
                    // Validate that the spec has valid encoding before adding
                    const hasValidEncoding = vis.spec.encoding &&
                                            Object.keys(vis.spec.encoding).length > 0 &&
                                            (vis.spec.encoding.x || vis.spec.encoding.y || vis.spec.encoding.theta);
                    
                    if (hasValidEncoding) {
                 
                        newMessages.push({
                            role: 'assistant',
                            type: 'chart',
                            data: vis.spec
                        });
                    } else {
                    }
                } else {
                }
            }
        } else {
        }
        
        // Process legacy charts (fallback for backward compatibility).
        if (charts && charts.length > 0) {
            for (const chart of charts) {
                const queryAttachment = chart.type === 'vega-lite-inferred' ? chart.queryAttachment : null;
                
                if (queryAttachment) {
                    processedAttachmentIds.add(queryAttachment.attachment_id);
                    try {
                        let tableData;
                        
                        // Check if we have pre-enriched data from multi-SPN response
                        if (preEnrichedAttachments && queryAttachment.query_result_data) {
                            tableData = formatDatabricksDataForTable(queryAttachment.query_result_data);
                        } else {
                            // Fetch from API as before - use Databricks conversation ID
                            const resultUrl = `/api/proxy/genie/spaces/${genieId}/conversations/${databricksConvId}/messages/${messageId}/attachments/${queryAttachment.attachment_id}/query-result`;
                            const resultResponse = await api.get(resultUrl);
                            tableData = formatDatabricksDataForTable(resultResponse.data);
                        }
                        
                        if (tableData.length > 0 && Object.keys(tableData[0]).length >= 2) {
                            const firstColumn = Object.keys(tableData[0])[0];
                            const secondColumn = Object.keys(tableData[0])[1];

                            const chartType = queryAttachment.mark || inferChartTypeFromDescription(queryAttachment.query.description) || 'bar';
                            const generatedSpec = {
                                "$schema": "https://vega.github.io/schema/vega-lite/v6.json",
                                "description": queryAttachment.query.description,
                                "data": { "values": tableData },
                                "mark": chartType,
                                "encoding": {
                                    "x": {"field": firstColumn, "type": "temporal", "timeUnit": "year", "axis": { "title": "Year", "format": "%Y" }},
                                    "y": {"field": secondColumn, "type": "quantitative"}
                                },
                                "width": "container",
                                "height": "container"
                            };
                            newMessages.push({ role: 'assistant', type: 'chart', data: generatedSpec });
                        } else if (tableData.length > 0) {
                            // Sort table rows to match chart bar order (descending by measure)
                            const sortedTableData = sortTableDataByMeasure(tableData);
                            // Preserve column order by storing headers separately
                            const headers = Object.keys(sortedTableData[0]);
                            newMessages.push({
                                role: 'assistant',
                                type: 'table',
                                data: sortedTableData,
                                headers: headers // Store the original column order
                            });
                        }
                    } catch (fetchError) {
                        newMessages.push({ role: 'assistant', content: 'Failed to load chart data.' });
                    }
                } else if (chart.spec) {
                    const spec = {
                        ...chart.spec,
                        width: "container",
                        height: "container"
                    };
                    newMessages.push({ role: 'assistant', type: 'chart', data: spec });
                }
            }
        }
        
        // Render tables for any query attachments that were NOT used for charts or text responses.
        if (attachments) {
            for (const attachment of attachments) {
                if (attachment.query && !processedAttachmentIds.has(attachment.attachment_id)) {
                    try {
                        let tableData;
                        
                        // Check if we have pre-enriched data from multi-SPN response
                        if (preEnrichedAttachments && attachment.query_result_data) {
                            tableData = formatDatabricksDataForTable(attachment.query_result_data);
                        } else {
                            // Fetch from API as before - use Databricks conversation ID
                            const resultUrl = `/api/proxy/genie/spaces/${genieId}/conversations/${databricksConvId}/messages/${messageId}/attachments/${attachment.attachment_id}/query-result`;
                            const resultResponse = await api.get(resultUrl);
                            tableData = formatDatabricksDataForTable(resultResponse.data);
                        }
                        
                        if (tableData.length > 0) {
                            // Sort table rows to match chart bar order (descending by measure)
                            const sortedTableData = sortTableDataByMeasure(tableData);
                            // Preserve column order by storing headers separately
                            const headers = Object.keys(sortedTableData[0]);
                            newMessages.push({
                                role: 'assistant',
                                type: 'table',
                                data: sortedTableData,
                                headers: headers // Store the original column order
                            });
                        }
                    } catch (fetchError) {
                         console.error('Failed to fetch data for fallback table:', fetchError);
                    }
                }
            }
        }

        // Extract and add suggested questions.
        if (attachments) {
            const suggestions = attachments
                .filter(att => att.suggested_questions && att.suggested_questions.questions)
                .flatMap(att => att.suggested_questions.questions);

            if (suggestions.length > 0) {
                newMessages.push({ role: 'assistant', type: 'suggestions', questions: suggestions });
            }
        }
        
        // Final check for empty response.
        if (newMessages.length === 0) {
             // Fallback to original content ONLY if nothing else was added.
            newMessages.push({ role: 'assistant', content: content || "Request processed, but no displayable content was returned." });
        }

        // Attach feedback metadata to the last assistant message in the group
        // so thumbs up/down can be shown after the full response
        const lastAssistantIdx = [...newMessages].reduceRight((found, m, i) => found !== -1 ? found : (m.role === 'assistant' ? i : -1), -1);
        if (lastAssistantIdx !== -1 && databricksConvId && messageId) {
            newMessages[lastAssistantIdx] = {
                ...newMessages[lastAssistantIdx],
                feedbackGroupId,
                feedbackConvId: databricksConvId,
                feedbackMsgId: messageId,
                feedbackSpaceId: genieId
            };
        }

        setMessages(prev => [...prev.filter(m => m.role !== 'status'), ...newMessages]);

    } catch (error) {
        console.error('Error calling /api/genie/ask:', error);
        
        // Handle different error types
        let errorMessage;
        const errorType = error.response?.data?.errorType;
        const status = error.response?.status;
        
        if (errorType === 'ALL_SPNS_FAILED') {
            // All service principals failed — show a friendly retry message
            errorMessage = "⚠️ **Temporarily Unable to Process Your Request**\n\n" +
                          "The data service encountered an issue while processing your question. " +
                          "This is usually a temporary problem — please try asking your question again in a moment.\n\n" +
                          "If the issue persists, please contact your system administrator.";
        } else if (errorType === 'SPN_AUTH_ERROR') {
            // SPN authentication error - show user-friendly message
            errorMessage = "⚠️ **Service Temporarily Unavailable**\n\n" +
                          "I'm unable to connect to the data service at this moment. " +
                          "This is a configuration issue, not a problem with your account.\n\n" +
                          "Please contact your system administrator or try again later.";
        } else if (errorType === 'PERMISSION_ERROR') {
            // Permission denied error
            errorMessage = "🔒 **Access Denied**\n\n" +
                          error.response?.data?.details ||
                          "The service account doesn't have permission to access the required data tables. " +
                          "This is a permissions configuration issue.\n\n" +
                          "Please contact your system administrator to grant the necessary access.";
        } else if (errorType === 'QUERY_FAILED') {
            // Query execution failed
            const details = error.response?.data?.details;
            errorMessage = "❌ **Query Failed**\n\n" +
                          (typeof details === 'string' ? details : "Your query couldn't be executed. Please try rephrasing your question or contact support if the issue persists.");
        } else if (status === 503) {
            // Service unavailable
            errorMessage = "⚠️ **Service Temporarily Unavailable**\n\n" +
                          error.response?.data?.details || "The service is temporarily unavailable. Please try again later.";
        } else if (status === 401) {
            // This is a real user auth error - the api.js interceptor will handle the redirect
            errorMessage = "Your session has expired. Please log in again.";
        } else if (status === 403) {
            // Forbidden - but not permission error (shouldn't normally happen)
            errorMessage = "🔒 **Access Forbidden**\n\n" +
                          error.response?.data?.details || "You don't have permission to access this resource.";
        } else {
            // Generic error
            errorMessage = error.response?.data?.error ||
                          error.response?.data?.details ||
                          "My apologies, I've encountered an issue. Please try again.";
        }
        
        setMessages(prev => [...prev.filter(m => m.role !== 'status'), { role: 'assistant', content: errorMessage }]);
    } finally {
        setIsWaitingForResponse(false);
        setIsProcessing(false);
        isProcessingRef.current = false; // Reset the guard
    }
// Bug 4: `messages` removed from deps — read via messagesRef instead.
// This prevents handleSend from being recreated on every response, which
// would silently break the stale-closure chain inside startNewChat.
}, [input, isWaitingForResponse, selectedGenie, genieId, activeConversationId, databricksConversationId, createConversation, updateConversationActivity, selectedClient, setIsProcessing]);

  const inferChartTypeFromDescription = (description) => {
    if (!description) return null;
    const desc = description.toLowerCase();
    if (desc.includes('line chart')) return 'line';
    if (desc.includes('bar chart')) return 'bar';
    if (desc.includes('area chart')) return 'area';
    if (desc.includes('scatter plot')) return 'point';
    if (desc.includes('pie chart')) return 'arc';
    return null;
  };

  /**
   * Sort table rows to match chart bar order: descending by the first numeric column.
   * This ensures the downloaded CSV and the on-screen table reflect the same ranking
   * as the bar chart (which is sorted by measure descending via Vega-Lite sort="-y").
   */
  const sortTableDataByMeasure = (tableData) => {
    if (!tableData || tableData.length === 0) return tableData;
    const keys = Object.keys(tableData[0]);
    // Find the first numeric column (the measure)
    const numericKey = keys.find(k => {
      const sample = tableData.find(r => r[k] != null)?.[k];
      if (sample == null) return false;
      if (typeof sample === 'number') return true;
      if (typeof sample === 'string') {
        const cleaned = sample.replace(/[$,\s€£¥]/g, '').trim();
        return !isNaN(cleaned) && cleaned !== '';
      }
      return false;
    });
    if (!numericKey) return tableData; // no numeric column found — leave as-is
    return [...tableData].sort((a, b) => {
      const av = parseFloat(String(a[numericKey]).replace(/[$,\s€£¥]/g, '')) || 0;
      const bv = parseFloat(String(b[numericKey]).replace(/[$,\s€£¥]/g, '')) || 0;
      return bv - av; // descending
    });
  };

  const formatDatabricksDataForTable = (data) => {
    // Support both structures:
    //   { statement_response: { manifest: ..., result: ... } }  ← proxy endpoint response
    //   { manifest: ..., result: ... }                          ← direct SDK getQueryResult response
    const sr = data?.statement_response || data;

    if (!sr?.manifest?.schema?.columns || !sr?.result?.data_array) {
      return [];
    }

    const headers = sr.manifest.schema.columns.map(col => col.name);
    const rows = sr.result.data_array;

    return rows.map(row => {
      const rowObject = {};
      headers.forEach((header, index) => {
        rowObject[header] = row[index];
      });
      return rowObject;
    });
  };

  const handleMicClick = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    // If already listening, stop
    if (isListening) {
      recognitionRef.current?.stop();
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setInput(transcript);
    };

    recognition.onerror = (event) => {
      console.error('[SPEECH] Recognition error:', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;
    };

    recognition.start();
  }, [isListening]);

  const startNewChat = useCallback((text = null) => {
    setMessages([]);
    setActiveConversationId(null);
    setDatabricksConversationId(null); // Reset Databricks conversation ID
    // Only send if text is provided and is a non-empty string
    if (text && typeof text === 'string' && text.trim() !== '') {
        handleSend(text);
    }
  }, [handleSend]);

  const loadHistoryConversation = useCallback(async (conversationId) => {
    try {
      setLoadingConversationId(conversationId);
      const data = await loadConversation(conversationId);
      
      // Guard: data.messages must be a non-empty array.
      // An empty array [] is truthy in JS, so checking only `data.messages`
      // would cause setMessages([]) → welcome screen shown instead of the chat.
      if (data && Array.isArray(data.messages) && data.messages.length > 0) {
    
        setActiveConversationId(conversationId);
        
        // Restore Databricks conversation ID from metadata
        if (data.databricks_conversation_id) {
          setDatabricksConversationId(data.databricks_conversation_id);
        }
        
        // Transform backend messages to component format
        const transformedMessages = data.messages.map((msg, index) => {
          
          const baseMsg = {
            role: msg.role
          };
          
          // Parse metadata if it exists
          let metadata = {};
          if (typeof msg.metadata === 'string') {
            try {
              metadata = JSON.parse(msg.metadata);
            } catch (e) {
              console.warn('Failed to parse metadata:', e);
            }
          } else if (msg.metadata) {
            metadata = msg.metadata;
          }
          
          // Handle different message types
          if (msg.message_type === 'chart') {
            if (metadata.data) {
              const spec = metadata.data;
              // Only render as chart if encoding is present with at least one axis
              const enc = spec.encoding || {};
              const hasEncoding = Object.keys(enc).length > 0 &&
                (enc.x || enc.y || enc.theta);
              if (hasEncoding) {
                baseMsg.type = 'chart';
                baseMsg.data = spec;
              } else {
                baseMsg.content = msg.content || '';
              }
            } else {
              baseMsg.content = msg.content || '';
            }
          } else if (msg.message_type === 'table') {
            // Handle table data from backend - check if we need to format it
            let formattedData = null;
            
            
            if (metadata.query_result) {
              // Backend stored raw query result - format it for display
              const qr = metadata.query_result;
              if (qr?.statement_response) {
              } else if (qr?.manifest) {
              }
              const fd = formatDatabricksDataForTable(qr);
              if (fd.length > 0) formattedData = fd;
            } else if (metadata.data && metadata.data.length > 0) {
              // Already formatted data
              formattedData = metadata.data;
            }
            
            if (formattedData) {
              // Sort table rows to match chart bar order (descending by measure)
              const sortedFormattedData = sortTableDataByMeasure(formattedData);
              baseMsg.type = 'table';
              baseMsg.data = sortedFormattedData;
              baseMsg.headers = metadata.headers || Object.keys(sortedFormattedData[0]);
            } else {
              // No renderable data — show description text instead of blank table
              baseMsg.content = msg.content || '';
            }
          } else if (msg.message_type === 'suggestions') {
            // Suggestions are ephemeral — skip them entirely when loading history
            return null;
          } else {
            // Text message
            baseMsg.content = msg.content || '';
          }
          
          return baseMsg;
        }).filter(Boolean); // remove nulls (skipped suggestions)
        
        setMessages(transformedMessages);
      } else {
        // Bug 3: Show an error message instead of silently resetting to the
        // welcome screen. The user clicked a history item and expects to see
        // the conversation — blank-slating the UI with no feedback is confusing.
        console.warn('[LOAD HISTORY] Conversation returned no messages — Databricks may have expired it.');
        setMessages([{
          role: 'assistant',
          content: '⚠️ **This conversation could not be loaded.**\n\nThe messages may no longer be available in Databricks. Please start a new chat.'
        }]);
      }
    } catch (error) {
      console.error('[LOAD HISTORY] Error loading conversation:', error);
      // Bug 3: Show error in chat window instead of silently calling startNewChat()
      setMessages([{
        role: 'assistant',
        content: '⚠️ **Failed to load conversation.**\n\nAn error occurred while retrieving this chat. Please try again or start a new chat.'
      }]);
    } finally {
      setLoadingConversationId(null);
    }
  }, [loadConversation]);

  // Handle deleting a conversation from the sidebar
  const handleDeleteConversation = useCallback(async (e, conversationId) => {
    // Stop the click from bubbling up to the <li> onClick (which would load the conversation)
    e.stopPropagation();
    if (deletingConversationId) return; // prevent double-click
    setDeletingConversationId(conversationId);
    try {
      const ok = await deleteConversation(conversationId);
      if (ok && conversationId === activeConversationId) {
        // The active conversation was deleted — reset to welcome screen
        setMessages([]);
        setActiveConversationId(null);
        setDatabricksConversationId(null);
      }
    } finally {
      setDeletingConversationId(null);
    }
  }, [deletingConversationId, deleteConversation, activeConversationId]);
  
  // Load conversations when component mounts or when genie/client changes.
  // Both genieId AND selectedClient are required to properly scope history to
  // the current user > client > genie combination.
  useEffect(() => {
    if (genieId) {
      loadConversations(genieId, selectedClient || null);
    }
  }, [genieId, selectedClient, loadConversations]);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  const formatTime = (isoString) => {
    const now = new Date();
    const messageTime = new Date(isoString);
    const diffInSeconds = Math.max(0, Math.floor((now - messageTime) / 1000));
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return diffInHours === 1 ? '1h ago' : `${diffInHours} hrs ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return diffInWeeks === 1 ? '1 week ago' : `${diffInWeeks} weeks ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
  };

  const handleGenieChange = (genieId) => {
    const genie = genies.find(g => g.id === genieId);
    if (genie) {
      setSelectedGenie(genie);
      setMessages([]);
      setActiveConversationId(null);
      setDatabricksConversationId(null);
      setIsDropdownOpen(false);
    }
  };

  // ── Export handlers ──────────────────────────────────────────────────────────

  // Close export dropdown when clicking outside
  useEffect(() => {
    if (!isExportOpen) return;
    const handleOutsideClick = (e) => {
      if (exportBtnRef.current && !exportBtnRef.current.contains(e.target)) {
        setIsExportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isExportOpen]);

  const handleExport = useCallback(async (format) => {
    setIsExportOpen(false);
    const displayName = getDisplayName();
    const genieName = selectedGenie?.name || 'AI Studio';
    if (format === 'pdf') {
      await exportAsPDF(messages, genieName, displayName);
    } else if (format === 'csv') {
      exportAsCSV(messages, genieName);
    } else if (format === 'txt') {
      exportAsTXT(messages, genieName, displayName);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, selectedGenie]);

  const handleCopyMessage = useCallback((msgIndex, msg) => {
    let textToCopy = '';
    if (msg.type === 'table' && msg.data && msg.headers) {
      // Tab-separated table
      const header = msg.headers.join('\t');
      const rows = msg.data.map(row => msg.headers.map(h => row[h] ?? '').join('\t'));
      textToCopy = [header, ...rows].join('\n');
    } else if (msg.type === 'chart') {
      textToCopy = msg.data?.description || 'Chart';
    } else {
      textToCopy = msg.content || '';
    }

    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopiedMsgIndex(msgIndex);
      setTimeout(() => setCopiedMsgIndex(null), 2000);
    }).catch(() => {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = textToCopy;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedMsgIndex(msgIndex);
      setTimeout(() => setCopiedMsgIndex(null), 2000);
    });
  }, []);

  const renderExportButton = () => (
    <div className="export-btn-wrap" ref={exportBtnRef}>
      <button
        type="button"
        className={`export-chat-btn${isExportOpen ? ' open' : ''}`}
        aria-label="Export conversation"
        title="Export conversation"
        onClick={() => setIsExportOpen(o => !o)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      </button>
      {isExportOpen && (
        <div className="export-dropdown-panel" role="menu">
          <div className="export-dropdown-item" role="menuitem" onClick={() => handleExport('pdf')}>
            <span className="export-item-icon">📄</span>
            <span className="export-item-label">Export as PDF</span>
          </div>
          <div className="export-dropdown-item" role="menuitem" onClick={() => handleExport('csv')}>
            <span className="export-item-icon">📊</span>
            <span className="export-item-label">Export as CSV</span>
          </div>
          <div className="export-dropdown-item" role="menuitem" onClick={() => handleExport('txt')}>
            <span className="export-item-icon">📝</span>
            <span className="export-item-label">Export as TXT</span>
          </div>
        </div>
      )}
    </div>
  );

  const renderGenieDropdown = () => (
    <div className="genie-dropdown-wrap">
      <button
        className={`genie-dropdown-trigger${isDropdownOpen ? ' open' : ''}`}
        onClick={() => !isProcessing && setIsDropdownOpen(o => !o)}
        disabled={!isAuthenticated || isProcessing}
        title={isProcessing ? 'Please wait for the current response to complete' : undefined}
        aria-haspopup="listbox"
        aria-expanded={isDropdownOpen}
      >
        <span className="genie-dropdown-trigger-text">
          {selectedGenie?.name || 'Select Genie'}
        </span>
        <svg className="genie-dropdown-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </button>
      {isDropdownOpen && !isProcessing && (
        <div className="genie-dropdown-panel" role="listbox">
          {genies.map(genie => (
            <div
              key={genie.id}
              className={`genie-dropdown-option${genie.id === selectedGenie?.id ? ' selected' : ''}`}
              role="option"
              aria-selected={genie.id === selectedGenie?.id}
              onClick={() => handleGenieChange(genie.id)}
            >
              {genie.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="chat-page-container">
      {loadingConversationId && (
        <div className="conversation-loading-overlay">
          <div className="conversation-loading-content">
            <div className="conversation-loading-spinner-large"></div>
            <div className="conversation-loading-text">Loading conversation...</div>
          </div>
        </div>
      )}
      <div className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <button onClick={() => navigate(-1)} className="back-button" aria-label="Back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          {!isSidebarCollapsed && <div className="sidebar-header-spacer" />}
          <button className="sidebar-toggle" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)} aria-label="Toggle sidebar">
            <svg width="20" height="20" viewBox="0 0 20 20"><rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/><rect x="4" y="4" width="6" height="2" rx="1" fill="currentColor"/><rect x="4" y="7" width="6" height="2" rx="1" fill="currentColor"/><rect x="4" y="10" width="6" height="2" rx="1" fill="currentColor"/></svg>
          </button>
        </div>
        {isSidebarCollapsed && (
          <button
            type="button"
            className="new-chat-button-collapsed"
            onClick={() => startNewChat()}
            aria-label="New Chat"
            title="New Chat"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
          </button>
        )}
        {!isSidebarCollapsed && (
          <>
            <div className="sidebar-title">
              <h3 className="sidebar-title-text">
                AI Studio
              </h3>
            </div>
            <div className="sidebar-actions">
              <div className="search-bar chat-sidebar-search">
                <svg className="search-bar-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                <input type="text" placeholder="Search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-bar-input" aria-label="Search" />
              </div>
              <button type="button" className="new-chat-button" onClick={() => startNewChat()}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                <span>New Chat</span>
              </button>
            </div>
            <div className="recent-history">
              <h4 className="recent-history-title">Recent Chats</h4>
              <ul className="recent-history-list">
                {conversations
                  .filter(conv => conv.title.toLowerCase().includes(searchQuery.toLowerCase()))
                  .slice(0, 10)
                  .map((conv) => {
                    const displayTitle = conv.title.length > 28 ? conv.title.slice(0, 28) + '...' : conv.title;
                    const isLoading = loadingConversationId === conv.id;
                    const isDeleting = deletingConversationId === conv.id;
                    return (
                      <li
                        key={conv.id}
                        onClick={() => !isLoading && !isDeleting && loadHistoryConversation(conv.id)}
                        className={`recent-chat-item ${isLoading ? 'loading' : ''}`}
                      >
                        <div className="history-item-text-container">
                          <img src="/icon-chat.svg" alt="" className="history-chat-icon" width="15" height="13" />
                          <span className="history-item-title" title={conv.title}>{displayTitle}</span>
                          {isLoading && <span className="history-loading-spinner" />}
                        </div>
                        <div className="history-item-actions">
                          <span className="time">{formatTime(conv.updated_at)}</span>
                          <button
                            type="button"
                            className="delete-conversation-btn"
                            aria-label="Delete conversation"
                            title="Delete conversation"
                            onClick={(e) => handleDeleteConversation(e, conv.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <span className="history-loading-spinner" style={{ marginLeft: 0 }} />
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6"/>
                                <path d="M14 11v6"/>
                                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                              </svg>
                            )}
                          </button>
                        </div>
                      </li>
                    );
                  })}
              </ul>
              {conversations.filter(conv => conv.title.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 10).length === 0 && (
                <p className="recent-history-empty">{conversations.length === 0 ? 'No recent chats yet' : 'No matches for search'}</p>
              )}
            </div>
            <div
              className="sidebar-footer-tools"
              style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ''}/BG-Chat.png)` }}
            >
              <p className="sidebar-footer-tools-title">Check out our other AI tools</p>
              <Link to="/genies" className="sidebar-learn-more-link">Learn More</Link>
            </div>
          </>
        )}
      </div>
      <div className={`main-chat-area ${isSidebarCollapsed ? 'expanded' : ''}`}>
        {messages.length === 0 ? (
          <div className="welcome-screen">
            <div className="welcome-top-row">
              <div className="welcome-message-block">
                <h1 className="welcome-title">Welcome Back!</h1>
                <p className="welcome-name">{getDisplayName()}</p>
              </div>
              <div className="genie-selector-header genie-selector-inline">
                {isGenieListLoading ? (
                  <p className="genie-selector-message">Loading...</p>
                ) : genies.length === 0 ? (
                  <div className="no-genies-message">
                    <p>No Genie mapped. Please contact Admin.</p>
                  </div>
                ) : (
                  renderGenieDropdown()
                )}
              </div>
            </div>
            {/* Initial questions for the selected genie */}
            {(() => {
              const questions = getInitialQuestions(selectedGenie?.name);
              if (!questions || questions.length === 0) return null;
              return (
                <div className="initial-questions-section">
                  <div className="predefined-questions">
                    {questions.map((q, i) => (
                      <button
                        key={i}
                        type="button"
                        className="predefined-question"
                        onClick={() => handleSend(q)}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <>
            <div className="chat-header-actions">
              {isGenieListLoading ? (
                <p className="genie-selector-message">Loading...</p>
              ) : genies.length === 0 ? (
                <div className="no-genies-message">
                  <p>No Genie mapped. Please contact Admin.</p>
                </div>
              ) : (
                renderGenieDropdown()
              )}
              {renderExportButton()}
            </div>
            <div className="chat-window">
              {messages.map((msg, index) => (
              <div key={index} className={`message ${msg.role}`}>
                {msg.type === 'table' ? (
                  <TableView data={msg.data} headers={msg.headers} />
                ) : msg.type === 'chart' ? (
                  <VegaChart spec={msg.data} />
                ) : msg.type === 'suggestions' ? (
                  <div className="suggested-questions">
                    {msg.questions.map((q, i) => (
                      <button key={i} className="predefined-question" onClick={() => handleSend(q)}>
                        {q}
                      </button>
                    ))}
                  </div>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content || ''}</ReactMarkdown>
                )}
                {/* Per-message copy button for assistant messages (non-status, non-suggestions) */}
                {msg.role === 'assistant' && msg.type !== 'suggestions' && (
                  <button
                    type="button"
                    className={`msg-copy-btn${copiedMsgIndex === index ? ' copied' : ''}`}
                    aria-label={copiedMsgIndex === index ? 'Copied!' : 'Copy message'}
                    title={copiedMsgIndex === index ? 'Copied!' : 'Copy to clipboard'}
                    onClick={() => handleCopyMessage(index, msg)}
                  >
                    {copiedMsgIndex === index ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
                    ) : (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    )}
                  </button>
                )}
                {/* Thumbs up/down – only on the last message of an assistant response group */}
                {msg.role === 'assistant' && msg.feedbackGroupId && (() => {
                  const gid = msg.feedbackGroupId;
                  const fb = feedbackState[gid];
                  return (
                    <div className="feedback-buttons">
                      <button
                        className={`feedback-btn feedback-btn-up${fb === 'POSITIVE' ? ' feedback-btn-active' : ''}`}
                        aria-label="Thumbs up"
                        title={fb === 'POSITIVE' ? 'Rated: Good response' : 'Good response'}
                        onClick={() => handleFeedback(gid, msg.feedbackSpaceId, msg.feedbackConvId, msg.feedbackMsgId, 'POSITIVE')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={fb === 'POSITIVE' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/>
                          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/>
                        </svg>
                      </button>
                      <button
                        className={`feedback-btn feedback-btn-down${fb === 'NEGATIVE' ? ' feedback-btn-active feedback-btn-active-neg' : ''}`}
                        aria-label="Thumbs down"
                        title={fb === 'NEGATIVE' ? 'Rated: Poor response' : 'Poor response'}
                        onClick={() => handleFeedback(gid, msg.feedbackSpaceId, msg.feedbackConvId, msg.feedbackMsgId, 'NEGATIVE')}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill={fb === 'NEGATIVE' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/>
                          <path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/>
                        </svg>
                      </button>
                      {fb && fb !== 'submitting' && (
                        <span className="feedback-thanks">
                          {fb === 'POSITIVE' ? 'Thanks for the feedback!' : 'Thanks, we\'ll improve!'}
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </>
        )}
        <div className="chat-input-wrapper">
          <div className="chat-input">
            <input type="text" className="chat-input-field" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()} placeholder={!selectedGenie ? "Select a Genie to begin" : "Ask your question"} disabled={!selectedGenie || isWaitingForResponse} aria-label="Ask your question" />
            <button
              type="button"
              className={`chat-input-mic${isListening ? ' listening' : ''}`}
              aria-label={isListening ? 'Stop recording' : 'Voice input'}
              title={isListening ? 'Click to stop recording' : 'Click to speak'}
              onClick={handleMicClick}
              disabled={!selectedGenie || isWaitingForResponse}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
            </button>
            <button type="button" className="chat-input-send" onClick={() => handleSend()} disabled={!selectedGenie || isWaitingForResponse} aria-label="Send">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>
            </button>
          </div>
          <p className="disclaimer">Always review the accuracy of responses.</p>
        </div>
      </div>
    </div>
  );
}

export default GenericChatPage;